import { CalendarPlottedEvent, EnterpriseCalendarEvent } from '../../services/enterpriseCalendarsApi';
import { isSameDay, startOfDay } from './calendarUtils';

export const RECURRENCE_TYPE_OPTIONS = [
  { value: 'None', label: 'Sem recorrência' },
  { value: 'Daily', label: 'Diária' },
  { value: 'Weekly', label: 'Semanal' },
  { value: 'Monthly', label: 'Mensal' },
  { value: 'Annual', label: 'Anual' },
  { value: 'CustomInterval', label: 'Personalizada — a cada N dias' },
  { value: 'CustomWeekday', label: 'Personalizada — dia da semana' },
] as const;

export const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Segunda-feira' },
  { value: 2, label: 'Terça-feira' },
  { value: 3, label: 'Quarta-feira' },
  { value: 4, label: 'Quinta-feira' },
  { value: 5, label: 'Sexta-feira' },
  { value: 6, label: 'Sábado' },
];

function parseDaysOfWeek(value?: string): number[] {
  if (!value) return [];
  return value
    .split(',')
    .map((token) => Number.parseInt(token.trim(), 10))
    .filter((day) => !Number.isNaN(day) && day >= 0 && day <= 6);
}

function normalizeRecurrenceType(event: EnterpriseCalendarEvent): string {
  if (event.recurrenceType) {
    return event.recurrenceType;
  }

  return event.isRecurringYearly ? 'Annual' : 'None';
}

export function eventOccursOnDay(event: EnterpriseCalendarEvent, day: Date): boolean {
  const anchor = new Date(event.eventDate);
  if (Number.isNaN(anchor.getTime())) {
    return false;
  }

  const target = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const anchorDay = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  const recurrenceType = normalizeRecurrenceType(event);

  if (recurrenceType === 'None') {
    return isSameDay(anchorDay, target);
  }

  if (target < anchorDay) {
    return false;
  }

  switch (recurrenceType) {
    case 'Daily':
      return true;
    case 'Weekly': {
      const days = parseDaysOfWeek(event.recurrenceDaysOfWeek);
      const weekday = days.length > 0 ? days : [anchorDay.getDay()];
      return weekday.includes(target.getDay());
    }
    case 'Monthly':
      return target.getDate() === anchorDay.getDate();
    case 'Annual':
      return target.getMonth() === anchorDay.getMonth() && target.getDate() === anchorDay.getDate();
    case 'CustomInterval': {
      if (!event.recurrenceInterval || event.recurrenceInterval <= 0) return false;
      const elapsedDays = Math.floor((target.getTime() - anchorDay.getTime()) / 86_400_000);
      return elapsedDays % event.recurrenceInterval === 0;
    }
    case 'CustomWeekday': {
      const days = parseDaysOfWeek(event.recurrenceDaysOfWeek);
      return days.includes(target.getDay());
    }
    default:
      return isSameDay(anchorDay, target);
  }
}

export function formatEventTimeLabel(event: EnterpriseCalendarEvent): string | null {
  if (event.isAllDay) {
    return null;
  }

  const start = (event.startTime ?? '').slice(0, 5);
  const end = (event.endTime ?? '').slice(0, 5);
  if (!start || !end) {
    return null;
  }

  return `${start} – ${end}`;
}

function taskOccursOnDay(event: CalendarPlottedEvent, day: Date): boolean {
  const startValue = event.eventDate?.slice(0, 10);
  if (!startValue) {
    return false;
  }

  const start = startOfDay(new Date(`${startValue}T00:00:00`));
  const endValue = event.taskEndDate?.slice(0, 10) ?? startValue;
  const end = startOfDay(new Date(`${endValue}T00:00:00`));
  const target = startOfDay(day);

  return target >= start && target <= end;
}

export function plottedEventOccursOnDay(event: CalendarPlottedEvent, day: Date): boolean {
  if (event.eventSource === 'tasks') {
    return taskOccursOnDay(event, day);
  }

  return eventOccursOnDay(event, day);
}
